import mixpanel from "mixpanel-browser";

class MixpanelService {
  constructor() {
    this.mixpanel = null;
    this.isInitialized = false;
  }

  init(token, options = {}) {
    if (this.isInitialized) {
      console.warn("Mixpanel is already initialized");
      return;
    }

    if (!token || token === "YOUR_TOKEN") {
      console.warn(
        "Mixpanel token is not provided or is placeholder. Mixpanel will not be initialized."
      );
      return;
    }

    try {
      // Default configuration with recommended settings
      const defaultOptions = {
        debug: false, // Disable verbose Mixpanel debug logs
        track_pageview: false, // We handle page tracking manually in Layout component
        persistence: "localStorage",
        secure_cookie: true,
        ...options,
      };

      mixpanel.init(token, defaultOptions);
      this.mixpanel = mixpanel;
      this.isInitialized = true;

      console.log("Mixpanel initialized successfully");
    } catch (error) {
      console.error("Failed to initialize Mixpanel:", error);
    }
  }

  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      console.warn(
        "Mixpanel is not initialized. Event not tracked:",
        eventName
      );
      return;
    }

    try {
      this.mixpanel.track(eventName, properties);
      // Simple log for development - just show the event name
      if (process.env.NODE_ENV === "development") {
        console.log(`📊 Mixpanel: ${eventName}`);
      }
    } catch (error) {
      console.error("Failed to track event:", eventName, error);
    }
  }

  identify(userId) {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. User not identified:", userId);
      return;
    }

    try {
      this.mixpanel.identify(userId);
    } catch (error) {
      console.error("Failed to identify user:", userId, error);
    }
  }

  setUserProperties(properties) {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. User properties not set");
      return;
    }

    try {
      this.mixpanel.people.set(properties);
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  alias(newId) {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. Alias not created");
      return;
    }

    try {
      this.mixpanel.alias(newId);
    } catch (error) {
      console.error("Failed to create alias:", error);
    }
  }

  reset() {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. Reset not performed");
      return;
    }

    try {
      this.mixpanel.reset();
    } catch (error) {
      console.error("Failed to reset Mixpanel:", error);
    }
  }

  // Utility method to track page views manually if needed
  trackPageView(pageName, properties = {}) {
    this.track("Page View", {
      page: pageName,
      ...properties,
    });
  }

  // Privacy compliance methods
  optOutTracking() {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. Opt-out not performed");
      return;
    }

    try {
      this.mixpanel.opt_out_tracking();
    } catch (error) {
      console.error("Failed to opt out of tracking:", error);
    }
  }

  optInTracking() {
    if (!this.isInitialized) {
      console.warn("Mixpanel is not initialized. Opt-in not performed");
      return;
    }

    try {
      this.mixpanel.opt_in_tracking();
    } catch (error) {
      console.error("Failed to opt in to tracking:", error);
    }
  }
}

// Export a singleton instance
const mixpanelService = new MixpanelService();
export default mixpanelService;
